import { z } from 'zod';

import { REGISTRATION_MODES } from '../enums';
import { zCurrency, zId, zMoneyNonNegative, zOptionalString, zPagination } from './common';

/* ─── Пользователи ──────────────────────────────────────────────────────── */

export const adminUserFilterSchema = zPagination.extend({
  search: z.string().trim().max(80).optional(),
  role: z.enum(['USER', 'SUPERADMIN']).optional(),
  planId: zId.optional(),
  blocked: z.coerce.boolean().optional(),
});
export type AdminUserFilter = z.infer<typeof adminUserFilterSchema>;

export const updateUserSchema = z.object({
  role: z.enum(['USER', 'SUPERADMIN']).optional(),
  planId: zId.nullish(),
  /** ISO-дата окончания тарифа; null — бессрочно. */
  planExpiresAt: z.string().datetime().nullish(),
  planNote: zOptionalString(300),
  /**
   * Точечное снятие лимитов поверх тарифа: ключ лимита → число или null
   * («без ограничений именно этому пользователю»).
   */
  limitsOverride: z.record(z.string(), z.number().int().min(0).nullable()).nullish(),
});
export type UpdateUserInput = z.infer<typeof updateUserSchema>;

/**
 * Разумный потолок продления — не столько защита от злоупотребления (это
 * действие администратора платформы), сколько от опечатки: значение,
 * уводящее `expiresAt` за пределы разумной календарной даты.
 */
export const MAX_PLAN_DAYS = 3650;

/**
 * Выдача тарифа через количество дней, а не точную дату (PB-004).
 *
 * Одно семантическое действие на запрос — не смешивать «+N дней» с точной
 * датой в одной форме: тогда в одном запросе было бы два источника истины
 * для `expiresAt`, и неясно, какой главнее. Ручной edge-case с точной датой
 * остаётся на старом `PATCH /admin/users/:id` («Дополнительно» в интерфейсе)
 * — это отдельный запрос на отдельное действие, а не третий вариант здесь.
 */
export const adminPlanDurationActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('addDays'),
    planId: zId,
    days: z.number().int().positive().max(MAX_PLAN_DAYS),
    idempotencyKey: z.string().trim().min(8).max(128),
  }),
  z.object({
    action: z.literal('setUnlimited'),
    planId: zId,
    idempotencyKey: z.string().trim().min(8).max(128),
  }),
]);
export type AdminPlanDurationAction = z.infer<typeof adminPlanDurationActionSchema>;

export const blockUserSchema = z.object({
  blocked: z.boolean(),
  reason: zOptionalString(300),
});
export type BlockUserInput = z.infer<typeof blockUserSchema>;

/** Удаление уносит все данные пользователя, поэтому логин вводится руками. */
export const deleteUserSchema = z.object({
  confirmUsername: z.string().trim().min(1),
});

/* ─── Тарифы ────────────────────────────────────────────────────────────── */

/** null в любом лимите — «без ограничений». */
const zLimit = z.number().int().min(0).max(1_000_000).nullable();

export const planSchema = z.object({
  code: z
    .string()
    .trim()
    .min(2)
    .max(30)
    .regex(/^[A-Z0-9_]+$/, { message: 'validation.planCode' }),
  name: z.string().trim().min(2).max(60),
  description: zOptionalString(300),

  isUnlimited: z.boolean().default(false),

  maxWorkspaces: zLimit.default(2),
  maxPeoplePerWorkspace: zLimit.default(12),
  maxWalletsPerWorkspace: zLimit.default(10),
  maxTeams: zLimit.default(3),
  maxRecurring: zLimit.default(20),
  maxStorageMb: zLimit.default(500),
  maxFileMb: z.number().int().min(1).max(200).default(15),

  priceMinor: zMoneyNonNegative.default(0),
  currency: zCurrency.default('RUB'),
  billingPeriod: z.enum(['FREE', 'MONTH', 'YEAR', 'LIFETIME']).default('FREE'),

  isDefaultForNewUsers: z.boolean().default(false),
  isActive: z.boolean().default(true),
  /**
   * Показывать на публичном лендинге.
   *
   * Отдельно от `isActive`: акционный или закрытый тариф продолжает
   * действовать для тех, кому назначен, но со страницы цен исчезает.
   * Через `isActive` это было не выразить — выключенный тариф перестаёт
   * работать вообще.
   */
  showOnLanding: z.boolean().default(true),
  sortOrder: z.number().int().min(0).max(999).default(0),
});
export type PlanInput = z.infer<typeof planSchema>;

export const updatePlanSchema = planSchema.partial();

/* ─── Технический журнал ────────────────────────────────────────────────── */

export const errorLogFilterSchema = zPagination.extend({
  source: z.enum(['http', 'job', 'telegram', 'backup', 'startup']).optional(),
  includeResolved: z.coerce.boolean().optional(),
  search: z.string().trim().max(200).optional(),
});
export type ErrorLogFilterInput = z.infer<typeof errorLogFilterSchema>;

export interface ErrorLogDto {
  id: string;
  level: string;
  source: string;
  message: string;
  stack: string | null;
  /** JSON-строка: метод, путь, код ответа, пользователь. */
  context: string | null;
  /** Сколько раз повторилась одна и та же ошибка. */
  count: number;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
}

export interface ErrorLogResponse {
  items: ErrorLogDto[];
  total: number;
  /** Сколько всего ждёт разбора — для значка в меню. */
  unresolved: number;
  page: number;
  pageSize: number;
}

/* ─── Настройки платформы ───────────────────────────────────────────────── */

export const systemSettingsSchema = z.object({
  registrationMode: z.enum(REGISTRATION_MODES).optional(),
  maxFileMb: z.number().int().min(1).max(200).optional(),
  defaultReminderDays: z.array(z.number().int().min(0).max(90)).max(6).optional(),
  defaultOverdueEveryDays: z.number().int().min(1).max(30).optional(),
  announcement: z
    .object({
      enabled: z.boolean(),
      text: z.string().trim().max(500),
      tone: z.enum(['info', 'warning']),
    })
    .nullish(),
  telegramEnabled: z.boolean().optional(),
  backupsEnabled: z.boolean().optional(),
  backupIntervalDays: z.number().int().min(1).max(90).optional(),
  backupRetentionDays: z.number().int().min(1).max(730).optional(),
  demoEnabled: z.boolean().optional(),
  demoLifetimeHours: z.number().int().min(1).max(720).optional(),
});
export type SystemSettingsInput = z.infer<typeof systemSettingsSchema>;

/* ─── Инвайты ───────────────────────────────────────────────────────────── */

// Платформенный инвайт на регистрацию — не путать с приглашением в воркспейс.
export const createPlatformInviteSchema = z.object({
  maxUses: z.number().int().min(1).max(1000).default(1),
  expiresAt: z.string().datetime().nullish(),
  note: zOptionalString(200),
  planId: zId.nullish(),
});
export type CreatePlatformInviteInput = z.infer<typeof createPlatformInviteSchema>;

/* ─── Аудит ─────────────────────────────────────────────────────────────── */

export const auditFilterSchema = zPagination.extend({
  actorId: zId.optional(),
  workspaceId: zId.optional(),
  action: z.string().trim().max(60).optional(),
  entityType: z.string().trim().max(40).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
});
export type AuditFilter = z.infer<typeof auditFilterSchema>;

/* ─── Воркспейсы ────────────────────────────────────────────────────────── */

export const adminWorkspaceFilterSchema = zPagination.extend({
  search: z.string().trim().max(80).optional(),
  includeDeleted: z.coerce.boolean().default(false),
});
export type AdminWorkspaceFilter = z.infer<typeof adminWorkspaceFilterSchema>;

/* ─── Ответы ────────────────────────────────────────────────────────────── */

export interface AdminOverviewDto {
  users: { total: number; blocked: number; newLast7Days: number; activeLast7Days: number };
  workspaces: { total: number; deleted: number; demo: number };
  data: { people: number; transactions: number; payouts: number; files: number };
  support: { open: number; waitingAdmin: number };
  jobs: { lastRun: string | null; lastStatus: string | null; failedLast7Days: number };
  storage: {
    usedMb: number;
    backups: number;
    lastBackupAt: string | null;
    /** Когда копия последний раз оказывалась за пределами этого сервера. */
    lastOffsiteBackupAt: string | null;
    /** Куда настроена выгрузка. Без ключей — только адрес. */
    offsite: string;
  };
  /** Предупреждения запуска: дефолтные секреты, отсутствие HTTPS и подобное. */
  warnings: string[];
}

export interface AdminUserDto {
  id: string;
  username: string;
  displayName: string;
  role: 'USER' | 'SUPERADMIN';
  isBlocked: boolean;
  blockedReason: string | null;
  isDemo: boolean;
  planId: string | null;
  planCode: string | null;
  planName: string | null;
  planExpiresAt: string | null;
  planNote: string | null;
  hasLimitsOverride: boolean;
  workspaceCount: number;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AdminUserListResponse {
  items: AdminUserDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface AdminWorkspaceDto {
  id: string;
  name: string;
  slug: string;
  baseCurrency: string;
  ownerId: string;
  ownerName: string;
  memberCount: number;
  peopleCount: number;
  transactionCount: number;
  storageMb: number;
  isDemo: boolean;
  createdAt: string;
  deletedAt: string | null;
}

export interface AdminWorkspaceListResponse {
  items: AdminWorkspaceDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface AuditEntryDto {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  actorId: string | null;
  actorName: string | null;
  workspaceId: string | null;
  workspaceName: string | null;
  payload: unknown;
  ip: string | null;
  createdAt: string;
}

export interface AuditListResponse {
  items: AuditEntryDto[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

export interface AdminPlanDto {
  id: string;
  code: string;
  name: string;
  description: string | null;
  isUnlimited: boolean;
  maxWorkspaces: number | null;
  maxPeoplePerWorkspace: number | null;
  maxWalletsPerWorkspace: number | null;
  maxTeams: number | null;
  maxRecurring: number | null;
  maxStorageMb: number | null;
  maxFileMb: number;
  priceMinor: number;
  currency: string;
  billingPeriod: string;
  isDefaultForNewUsers: boolean;
  isActive: boolean;
  showOnLanding: boolean;
  isSystem: boolean;
  sortOrder: number;
  /** Сколько пользователей сейчас на тарифе — удалять непустой нельзя. */
  userCount: number;
}

export interface AdminInviteDto {
  id: string;
  code: string;
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  note: string | null;
  planId: string | null;
  planName: string | null;
  disabledAt: string | null;
  createdAt: string;
  createdByName: string | null;
}

export interface ResetPasswordResponse {
  /** Временный пароль показывается один раз — в базе только его хэш. */
  password: string;
}

export interface MaintenanceResult {
  action: string;
  affected: number;
  message: string;
}

/**
 * Резервная копия.
 *
 * `verified` — сошлись ли контрольные суммы из манифеста. «Копия есть» и
 * «копия целая» — разные утверждения, а администратору нужно второе.
 */
export interface BackupRecordDto {
  id: string;
  fileName: string;
  sizeBytes: number;
  kind: 'AUTO' | 'MANUAL';
  createdAt: string;
  verified: boolean;
  problem: string | null;
  /** Копия цела, но отражает уже потерянные файлы. Не то же, что поломка. */
  warning: string | null;
  /**
   * Уехала ли копия за пределы этого сервера.
   * `NONE` — выгрузка не настроена или не выполнялась: такая копия лежит на
   * том же диске, что и база, и потерю сервера не переживёт.
   */
  offsiteStatus: 'NONE' | 'UPLOADED' | 'FAILED';
  offsiteAt: string | null;
  offsiteError: string | null;
}
